/**
 * CSV parsing and writing for the finance bulk import. Pure functions, no React.
 *
 * Parsing follows RFC 4180 as Excel writes it: fields may be quoted, a quoted
 * field may contain the separator, line breaks and doubled quotes (""). The
 * separator is ';' (German Excel) or ',' and is taken from the header line.
 */

export type CsvSeparator = ';' | ',';

export type CsvRecord = {
  /** 1 = the first record after the header; skipped empty records are counted. */
  rowNumber: number;
  values: string[];
};

export type ParsedCsv = {
  separator: CsvSeparator;
  header: string[];
  records: CsvRecord[];
};

const BOM = '﻿';

export function stripBom(text: string): string {
  return text.startsWith(BOM) ? text.slice(1) : text;
}

/** Counts ';' and ',' in the first line, outside quotes; ';' wins a tie. */
export function detectSeparator(text: string): CsvSeparator {
  let semicolons = 0;
  let commas = 0;
  let inQuotes = false;
  for (const char of text) {
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (!inQuotes && (char === '\n' || char === '\r')) {
      break;
    } else if (!inQuotes && char === ';') {
      semicolons += 1;
    } else if (!inQuotes && char === ',') {
      commas += 1;
    }
  }
  return commas > semicolons ? ',' : ';';
}

/** Splits the text into records of raw fields. */
export function parseRecords(
  text: string,
  separator: CsvSeparator,
): string[][] {
  const records: string[][] = [];
  let record: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;

  const endField = () => {
    record.push(field);
    field = '';
  };
  const endRecord = () => {
    endField();
    records.push(record);
    record = [];
  };

  while (i < text.length) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
      } else {
        field += char;
      }
      i += 1;
      continue;
    }
    if (char === '"') {
      inQuotes = true;
    } else if (char === separator) {
      endField();
    } else if (char === '\r') {
      endRecord();
      if (text[i + 1] === '\n') {
        i += 1;
      }
    } else if (char === '\n') {
      endRecord();
    } else {
      field += char;
    }
    i += 1;
  }
  // A last record without a trailing line break.
  if (field !== '' || record.length > 0) {
    endRecord();
  }
  return records;
}

const isEmptyRecord = (values: string[]) =>
  values.every((value) => value.trim() === '');

/**
 * Parses a CSV file. Returns null when the file has no header line. Fully
 * empty records are dropped, but still counted in rowNumber so the number
 * stays the row the record has in a spreadsheet (the header is row 1).
 */
export function parseCsv(input: string): ParsedCsv | null {
  const text = stripBom(input);
  const separator = detectSeparator(text);
  const all = parseRecords(text, separator);
  const headerIndex = all.findIndex((values) => !isEmptyRecord(values));
  if (headerIndex === -1) {
    return null;
  }
  const records: CsvRecord[] = [];
  all.slice(headerIndex + 1).forEach((values, index) => {
    if (!isEmptyRecord(values)) {
      records.push({ rowNumber: headerIndex + index + 2, values });
    }
  });
  return { separator, header: all[headerIndex], records };
}

/** The header as the server reads it: trimmed, without BOM, lower case. */
export function normalizeHeader(header: string): string {
  return stripBom(header.trim()).trim().toLowerCase();
}

function escapeField(value: string, separator: CsvSeparator): string {
  if (
    value.includes(separator) ||
    value.includes('"') ||
    value.includes('\n') ||
    value.includes('\r') ||
    value !== value.trim()
  ) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/** Writes a CSV with a BOM and CRLF line breaks, so Excel reads it as UTF-8. */
export function serializeCsv(
  header: string[],
  rows: string[][],
  separator: CsvSeparator = ';',
): string {
  const lines = [header, ...rows].map((row) =>
    row.map((value) => escapeField(value ?? '', separator)).join(separator),
  );
  return BOM + lines.join('\r\n') + '\r\n';
}

export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Decodes the file as UTF-8; a file that is not valid UTF-8 is read as
 * Windows-1252, which is what Excel writes for "CSV (Trennzeichen-getrennt)".
 */
export function decodeCsvBytes(buffer: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder('windows-1252').decode(buffer);
  }
}
